import {useState, useCallback, useEffect} from 'react'
import {createServiceProxy} from 'background-removal'

const service = createServiceProxy({modelId: 'rmbg-1.4'})
let warmedUp = false

/** Shared service for Sidebar warmup */
export {service as backgroundRemovalService}

/**
 * Hook to run background removal on an image URL using the background-removal package.
 * Model is loaded from CDN via modelId (no local model file).
 * Returns status, blob (on success), error, and retry function.
 * @param {string} imageSrc - node.content.src (URL, blob:, or data:)
 * @param {{ runWhen?: boolean }} [options] - when true, run processing (e.g. when modal is open)
 * @returns {{ status: 'idle'|'processing'|'preview'|'error', blob: Blob|null, error: Error|null, retry: () => void }}
 */
export function useBackgroundRemoval(imageSrc, options = {}) {
    const {runWhen = true} = options
    const [status, setStatus] = useState('idle')
    const [blob, setBlob] = useState(null)
    const [error, setError] = useState(null)

    const run = useCallback(async () => {
        if (!imageSrc) {
            setStatus('idle')
            setBlob(null)
            setError(null)
            return
        }

        setStatus('processing')
        setError(null)
        setBlob(null)

        try {
            if (!warmedUp) {
                await service.warmup()
                warmedUp = true
            }
            const result = await service.removeBackground(imageSrc)
            setBlob(result.blob)
            setStatus('preview')
        } catch (err) {
            setError(err)
            setStatus('error')
        }
    }, [imageSrc])

    useEffect(() => {
        if (runWhen && imageSrc) {
            run()
        }
    }, [imageSrc, runWhen, run])

    const retry = useCallback(() => run(), [run])

    return {status, blob, error, retry}
}
